import IProcessed_Report from "@models/IProcessed_Report";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import IOnGetCustomerNotesReviewReportDTO, { ICustomerNoteReviewItem } from "@models/DTO/IOnGetCustomerNotesReviewReportDTO";
import { useAppContextNonNull } from "@contexts/AppContext";
import { Link } from "react-router-dom";
import moment from "moment-timezone";
import * as _ from "lodash-es";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwButton from "../../../shared/OwButton/OwButton";
import OwSelectOption from "../../../shared/OwSelectOption/OwSelectOption";
import UserContext from "../../../../contexts/UserContext";
import { SocketContext } from "../../../../contexts/SocketContext";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IDownloadReportExcelDTO from "../../../../models/DTO/IDownloadReportExcelDTO";
import IOnDownloadReportExcelDTO from "../../../../models/DTO/IOnDownloadReportExcelDTO";
import UserService from "../../../../services/UserService";
import UIService from "../../../../services/UIService";
import "./CustomerNotesReview.scss";
import SupplierService from "@services/SupplierService";

type Props = {
    processedReport: IProcessed_Report;
};

type ViewMode = "date" | "vendor";

export const CustomerNotesReview = (props: Props) => {
    const { supplierModel } = useAppContextNonNull();
    const userModel = useContext(UserContext);
    const socket = useContext(SocketContext)!;

    const reportData = props.processedReport.report_data as IOnGetCustomerNotesReviewReportDTO;

    // State
    const [notes, setNotes] = useState<ICustomerNoteReviewItem[]>([]);
    const [filteredNotes, setFilteredNotes] = useState<ICustomerNoteReviewItem[]>([]);
    const [viewMode, setViewMode] = useState<ViewMode>("date");
    const [downloadingExcel, setDownloadingExcel] = useState(false);

    // Filter state
    const [vendorOptions, setVendorOptions] = useState<{ name: string; guid: string }[]>([]);
    const [userOptions, setUserOptions] = useState<{ name: string }[]>([]);
    const [selectedVendor, setSelectedVendor] = useState<string>("");
    const [selectedUser, setSelectedUser] = useState<string>("");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");

    // Initialize data
    useEffect(() => {
        if (reportData?.notes) {
            const sortedNotes = [...reportData.notes].sort((a, b) => {
                return moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf();
            });
            setNotes(sortedNotes);
            setFilteredNotes(sortedNotes);

            // Extract unique vendors
            const uniqueVendors = _.uniqBy(
                reportData.notes.map((n) => ({ name: n.vendorName, guid: n.vendorGuid })),
                "guid"
            );
            setVendorOptions(uniqueVendors);

            // Extract unique users
            const uniqueUsers = _.uniq(reportData.notes.map((n) => n.createdByUserName)).map((name) => ({ name }));
            setUserOptions(uniqueUsers);
        }
    }, [reportData]);

    // Socket listeners for Excel download
    useEffect(() => {
        socket.on(EndpointsEnum.ReportOnDownloadExcel, onDownloadExcel);

        return () => {
            socket.removeListener(EndpointsEnum.ReportOnDownloadExcel, onDownloadExcel);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Excel download handlers
    const handleDownloadExcel = () => {
        setDownloadingExcel(true);

        const payload: IDownloadReportExcelDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            reportGuid: props.processedReport.guid,
        };

        socket.emit(EndpointsEnum.ReportDownloadExcel, payload);
    };

    const onDownloadExcel = (data: IOnDownloadReportExcelDTO) => {
        setDownloadingExcel(false);

        if (data.downloadDirect && data.buffer) {
            downloadFromBase64(data.buffer, data.filename);
        } else if (data.s3Url) {
            window.open(data.s3Url, "_blank");
        }
    };

    const downloadFromBase64 = (base64: string, filename: string) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    // Filter handlers
    const handleVendorChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setSelectedVendor(e.target.value);
    };

    const handleUserChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setSelectedUser(e.target.value);
    };

    const handleStartDateChange = (e: ChangeEvent<HTMLInputElement>) => {
        setStartDate(e.target.value);
    };

    const handleEndDateChange = (e: ChangeEvent<HTMLInputElement>) => {
        setEndDate(e.target.value);
    };

    const applyFilters = () => {
        let filtered = [...notes];

        if (selectedVendor) {
            filtered = filtered.filter((n) => n.vendorGuid === selectedVendor);
        }

        if (selectedUser) {
            filtered = filtered.filter((n) => n.createdByUserName === selectedUser);
        }

        if (startDate) {
            const start = moment(startDate).startOf("day");
            filtered = filtered.filter((n) => moment(n.createdAt).isSameOrAfter(start));
        }

        if (endDate) {
            const end = moment(endDate).endOf("day");
            filtered = filtered.filter((n) => moment(n.createdAt).isSameOrBefore(end));
        }

        setFilteredNotes(filtered);
    };

    const clearFilters = () => {
        setSelectedVendor("");
        setSelectedUser("");
        setStartDate("");
        setEndDate("");
        setFilteredNotes(notes);
    };

    // Group notes by vendor for vendor view
    const groupedByVendor = _.groupBy(filteredNotes, "vendorGuid");

    // Render a single note card
    const renderNoteCard = (note: ICustomerNoteReviewItem) => {
        return (
            <div key={note.noteGuid} className={"note-card mb-3 " + (UIService.isMainViewLight() ? "" : "bg-dark text-light")}>
                <div className="d-flex flex-column">
                    <div className="d-flex align-items-center mb-1">
                        <Link to={`/account/${note.vendorGuid}/detail`} className="fw-bold me-3">
                            {note.vendorName}
                        </Link>
                        {note.contactName && (
                            <span className="text-muted">
                                <i className="fas fa-user me-1" />
                                {note.contactName}
                            </span>
                        )}
                    </div>
                    {(note.contactPhone || note.contactEmail) && (
                        <div className="text-small text-muted mb-1">
                            {note.contactPhone && (
                                <span className="me-3">
                                    <i className="fas fa-phone me-1" />
                                    {note.contactPhone}
                                </span>
                            )}
                            {note.contactEmail && (
                                <span>
                                    <i className="fas fa-envelope me-1" />
                                    <a href={`mailto:${note.contactEmail}`}>{note.contactEmail}</a>
                                </span>
                            )}
                        </div>
                    )}
                    <div className="note-content mb-2">{note.note}</div>
                    <div className="text-small text-muted">
                        <i className="fas fa-user-edit me-1" />
                        {note.createdByUserName}
                        <span className="mx-2">|</span>
                        <i className="fas fa-calendar me-1" />
                        {UserService.parseDate(note.createdAt as string)}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="CustomerNotesReview">
            {/* Header with Excel download and view toggle */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <OwLabel
                        className="me-3"
                        title={downloadingExcel ? "generating_excel" : "excel"}
                        leftIconClass={downloadingExcel ? "fas fa-spinner fa-spin" : "fas fa-download"}
                        fn={handleDownloadExcel}
                    />
                </div>
                <div className="btn-group">
                    <OwButton
                        title="by_date"
                        fn={() => setViewMode("date")}
                        className={viewMode === "date" ? "btn-primary" : "btn-outline-primary"}
                    />
                    <OwButton
                        title={SupplierService.updateStringWithVendorOverride("by_vendor", supplierModel)}
                        fn={() => setViewMode("vendor")}
                        className={viewMode === "vendor" ? "btn-primary" : "btn-outline-primary"}
                    />
                </div>
            </div>

            {/* Totals */}
            <div className="mb-3">
                <OwLabel title="total_notes" postText=":" className="me-2" />
                <span className="me-4">{reportData?.totals?.totalNotes || 0}</span>
                <OwLabel
                    title={SupplierService.updateStringWithVendorOverride("total_vendors", supplierModel)}
                    postText=":"
                    className="me-2"
                />
                <span>{reportData?.totals?.totalVendors || 0}</span>
            </div>

            {/* Filters */}
            <div className="d-flex flex-wrap align-items-end gap-2 mb-4">
                <div>
                    <OwLabel title={SupplierService.updateStringWithVendorOverride("vendor", supplierModel)} className="mb-1" />
                    <OwSelectOption
                        fn={handleVendorChange}
                        name="vendor"
                        titleProp="name"
                        valueProp="guid"
                        options={vendorOptions}
                        selectedOption={selectedVendor}
                        hasSelectedOptionProp={false}
                    />
                </div>
                <div>
                    <OwLabel title="created_by" className="mb-1" />
                    <OwSelectOption
                        fn={handleUserChange}
                        name="user"
                        titleProp="name"
                        valueProp="name"
                        options={userOptions}
                        selectedOption={selectedUser}
                        hasSelectedOptionProp={false}
                    />
                </div>
                <div>
                    <OwLabel title="start_date" className="mb-1" />
                    <input type="date" className="form-control form-control-sm" value={startDate} onChange={handleStartDateChange} />
                </div>
                <div>
                    <OwLabel title="end_date" className="mb-1" />
                    <input type="date" className="form-control form-control-sm" value={endDate} onChange={handleEndDateChange} />
                </div>
                <OwButton title="filter" fn={applyFilters} className="btn-primary" />
                <OwButton title="clear" fn={clearFilters} className="btn-danger" />
            </div>

            {/* Notes display */}
            {filteredNotes.length === 0 ? (
                <div className="text-muted">
                    <OwLabel title="no_notes_found" />
                </div>
            ) : viewMode === "date" ? (
                // By Date View
                <div className="notes-list">{filteredNotes.map((note) => renderNoteCard(note))}</div>
            ) : (
                // By Vendor View
                <div className="notes-by-vendor">
                    {Object.entries(groupedByVendor).map(([vendorGuid, vendorNotes]) => {
                        const vendorName = vendorNotes[0]?.vendorName || "";
                        return (
                            <div key={vendorGuid} className="vendor-group mb-4">
                                <div className="vendor-header d-flex align-items-center mb-2">
                                    <Link to={`/account/${vendorGuid}/detail`} className="fw-bold me-2">
                                        {vendorName}
                                    </Link>
                                    <span className="badge bg-secondary">{vendorNotes.length}</span>
                                </div>
                                <div className="vendor-notes ms-3">
                                    {vendorNotes
                                        .sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf())
                                        .map((note) => renderNoteCard(note))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
